/**
 * Inbound Shipment Detail Drawer
 *
 * Shows shipment progress, purchase order context, weighbridge readings, and SAP sync state.
 */

import { useEffect, useRef, useState } from 'react'
import type { ElementType, KeyboardEvent, ReactNode } from 'react'
import {
  useTranslation,
  useIsRTL,
  useForm,
  zodResolver,
  z,
  LICENSE_PLATE_DIGITS_LENGTH,
  LICENSE_PLATE_LETTERS_LENGTH,
  normalizeLicensePlateDigits,
  normalizeLicensePlateLetters,
  splitLicensePlate,
  updateLicensePlateDigits,
  updateLicensePlateLetters,
} from '@spcc/core'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  BaseDrawer,
  Badge,
  Button,
  Card,
  CheckCircle,
  Clock,
  Edit,
  FileText,
  History,
  Input,
  Label,
  Loader2,
  Package,
  RefreshCw,
  Save,
  Scale,
  Truck,
  X,
  XCircle,
  cn,
} from '@spcc/uikit'
import { sapSyncApi, SyncJobsTable, SyncJobDetailDrawer } from '@spcc/domains/sap-sync'
import type { SapOperation, SapSyncJob } from '@spcc/domains/sap-sync'
import type { InboundShipment, InboundShipmentStatus, InboundSapSyncStatus } from '../../domain/types'
import {
  fetchInboundShipmentTimeline,
  type InboundShipmentTimelineEventDto,
} from '../../infrastructure/api/inbound-shipments-api'

interface StatusConfig {
  label: string
  className: string
}

interface DetailRowProps {
  label: string
  value: ReactNode
  className?: string
}

interface MetricCardProps {
  label: string
  value: string
  icon: ElementType
}

interface SectionCardProps {
  icon: ElementType
  title: string
  action?: ReactNode
  children: ReactNode
}

interface TimelineLabels {
  title: string
  empty: string
  loadError: string
  success: string
  failed: string
  refresh: string
  actions: Record<string, string>
}

export interface UpdateInboundShipmentPayload {
  documentId: string
  vehiclePlate?: string | null
  driverName?: string | null
}

interface InboundShipmentDetailDrawerProps {
  shipment: InboundShipment | null
  isOpen: boolean
  onClose: () => void
  onUpdate?: (payload: UpdateInboundShipmentPayload) => void
  isUpdating?: boolean
}

type InboundShipmentDrawerTab = 'overview' | 'timeline' | 'sap-sync'

const STATUS_FLOW: InboundShipmentStatus[] = [
  'arrived',
  'first_weight_done',
  'unloaded',
  'second_weight_done',
  'grn_posted',
]

const INBOUND_SAP_OPERATIONS: SapOperation[] = [
  'inbound_weight_first',
  'inbound_weight_second',
  'create_goods_receipt',
]

const INBOUND_SHIPMENT_SAP_SYNC_TAB_STORAGE_KEY = 'spcc:inbound-shipment:show-sap-sync-tab'

function shouldShowInboundShipmentSapSyncTab(): boolean {
  if (typeof window === 'undefined') return false

  const value = window.localStorage.getItem(INBOUND_SHIPMENT_SAP_SYNC_TAB_STORAGE_KEY)
  return value === 'true' || value === '1' || value === 'enabled'
}

function getShipmentStatusConfig(status: InboundShipmentStatus, labels: Record<string, string>): StatusConfig {
  const classMap: Record<InboundShipmentStatus, string> = {
    arrived: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    first_weight_done: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    unloaded: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    second_weight_done: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    grn_posted: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    grn_skipped: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  }

  return {
    label: labels[status] ?? status,
    className: classMap[status],
  }
}

function getSapSyncClass(status: InboundSapSyncStatus | null | undefined): string {
  const classMap: Record<InboundSapSyncStatus, string> = {
    pending: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    first_weight_synced: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    second_weight_synced: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    grn_synced: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  }

  return status ? classMap[status] : ''
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function formatWeight(weight: number | null | undefined): string {
  if (weight == null) return '-'
  return `${weight.toLocaleString()} kg`
}

function getCurrentStepIndex(status: InboundShipmentStatus): number {
  return STATUS_FLOW.indexOf(status)
}

function buildSapEntityIds(shipment: InboundShipment): string[] {
  const ids: string[] = [shipment.shipmentNumber, String(shipment.id)]

  if (shipment.documentId) {
    ids.push(shipment.documentId)
  }

  if (shipment.goodsReceipt?.id != null) {
    ids.push(String(shipment.goodsReceipt.id))
  }

  if (shipment.goodsReceipt?.grnNumber) {
    ids.push(shipment.goodsReceipt.grnNumber)
  }

  return Array.from(new Set(ids.filter(Boolean)))
}

function getTimelineLabels(detail: Record<string, unknown>): TimelineLabels {
  return {
    title: typeof detail.timeline === 'string' ? detail.timeline : 'Timeline',
    empty: typeof detail.timelineEmpty === 'string' ? detail.timelineEmpty : 'No timeline events yet',
    loadError: typeof detail.timelineLoadError === 'string' ? detail.timelineLoadError : 'Unable to load timeline',
    success: typeof detail.timelineSuccess === 'string' ? detail.timelineSuccess : 'Success',
    failed: typeof detail.timelineFailed === 'string' ? detail.timelineFailed : 'Failed',
    refresh: typeof detail.timelineRefresh === 'string' ? detail.timelineRefresh : 'Refresh',
    actions: isRecord(detail.timelineActions) ? getStringRecord(detail.timelineActions) : {},
  }
}

function getStringRecord(value: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {}

  for (const [key, item] of Object.entries(value)) {
    if (typeof item === 'string') {
      result[key] = item
    }
  }

  return result
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getTimelineErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'Failed to load timeline'
}

function getTimelineActionLabel(action: string, labels: Record<string, string>): string {
  return labels[action] ?? action
}

function getTimelineSuccessClass(success: boolean | undefined): string {
  if (success === true) {
    return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
  }
  if (success === false) {
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
}

function getTimelineEventStatus(event: InboundShipmentTimelineEventDto): boolean | null {
  return typeof event.metadata?.success === 'boolean' ? event.metadata.success : null
}

function getTimelineEventSubtitle(event: InboundShipmentTimelineEventDto): string {
  const parts = [
    event.metadata?.step,
    event.metadata?.direction,
    event.metadata?.weightStage,
    event.metadata?.weightKg != null ? `${event.metadata.weightKg.toLocaleString()} kg` : null,
  ]

  return parts.filter((part): part is string => typeof part === 'string' && part.length > 0).join(' - ')
}

function DetailRow({ label, value, className }: DetailRowProps) {
  return (
    <div className={cn('grid grid-cols-[140px_1fr] gap-3 py-2 border-b border-border/50 last:border-0', className)}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 text-sm font-medium text-end break-words">{value ?? '-'}</span>
    </div>
  )
}

function MetricCard({ label, value, icon: Icon }: MetricCardProps) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-sm font-semibold truncate">{value}</p>
        </div>
      </div>
    </Card>
  )
}

function SectionCard({ icon: Icon, title, action, children }: SectionCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </Card>
  )
}

interface ShipmentInfoLabels {
  shipmentInfo: string
  vehiclePlate: string
  driverName: string
  arrivedAt: string
  departedAt: string
  factory: string
  storageLocation: string
  edit: string
  save: string
  cancel: string
}

interface ShipmentInfoFormValues {
  vehiclePlate: string
  driverName: string
}

const shipmentInfoSchema = z.object({
  vehiclePlate: z.string(),
  driverName: z.string(),
})

function getShipmentInfoDefaults(shipment: InboundShipment): ShipmentInfoFormValues {
  return {
    vehiclePlate: shipment.vehiclePlate ?? '',
    driverName: shipment.driverName ?? '',
  }
}

export function ShipmentInfoSection({
  shipment,
  labels,
  onUpdate,
  isUpdating,
}: {
  shipment: InboundShipment
  labels: ShipmentInfoLabels
  onUpdate?: (payload: UpdateInboundShipmentPayload) => void
  isUpdating?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const plateDigitsRef = useRef<HTMLInputElement | null>(null)
  const plateLettersRef = useRef<HTMLInputElement | null>(null)

  const { register, watch, setValue, reset, handleSubmit } = useForm<ShipmentInfoFormValues>({
    resolver: zodResolver(shipmentInfoSchema),
    defaultValues: getShipmentInfoDefaults(shipment),
  })
  const vehiclePlate = watch('vehiclePlate')

  useEffect(() => {
    reset(getShipmentInfoDefaults(shipment))
    setIsEditing(false)
  }, [shipment, reset])

  const canEdit = Boolean(onUpdate && shipment.documentId && shipment.status === 'arrived')
  const plateParts = splitLicensePlate(vehiclePlate)

  function handlePlateDigitsChange(value: string) {
    const digits = normalizeLicensePlateDigits(value)
    setValue('vehiclePlate', updateLicensePlateDigits(vehiclePlate, digits))
    if (digits.length === LICENSE_PLATE_DIGITS_LENGTH) {
      plateLettersRef.current?.focus()
    }
  }

  function handlePlateLettersChange(value: string) {
    setValue('vehiclePlate', updateLicensePlateLetters(vehiclePlate, normalizeLicensePlateLetters(value)))
  }

  function handlePlateLettersKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && plateParts.letters.length === 0) {
      plateDigitsRef.current?.focus()
    }
  }

  const submitShipmentInfo = handleSubmit((values) => {
    if (!onUpdate || !shipment.documentId) return

    onUpdate({
      documentId: shipment.documentId,
      vehiclePlate: values.vehiclePlate.trim() || null,
      driverName: values.driverName.trim() || null,
    })
    setIsEditing(false)
  })

  const action = !canEdit ? null : isEditing ? (
    <div className="flex gap-1">
      <Button variant="ghost" size="sm" className="h-7 px-2" disabled={isUpdating} onClick={() => setIsEditing(false)} aria-label={labels.cancel}>
        <X className="h-3.5 w-3.5" />
      </Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-primary" disabled={isUpdating} onClick={submitShipmentInfo} aria-label={labels.save}>
        <Save className="h-3.5 w-3.5" />
      </Button>
    </div>
  ) : (
    <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2.5" onClick={() => setIsEditing(true)}>
      <Edit className="h-3.5 w-3.5" />
      {labels.edit}
    </Button>
  )

  return (
    <SectionCard icon={Truck} title={labels.shipmentInfo} action={action}>
      {isEditing ? (
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{labels.vehiclePlate}</Label>
            <div className="flex items-center gap-2" dir="ltr">
              <Input
                ref={plateDigitsRef}
                value={plateParts.digits}
                onChange={(e) => handlePlateDigitsChange(e.target.value)}
                inputMode="numeric"
                pattern="[0-9]{1,4}"
                maxLength={LICENSE_PLATE_DIGITS_LENGTH}
                className="h-9 flex-1 text-center font-mono tracking-widest"
              />
              <span className="text-muted-foreground font-semibold">-</span>
              <Input
                ref={plateLettersRef}
                value={plateParts.letters}
                onChange={(e) => handlePlateLettersChange(e.target.value)}
                onKeyDown={handlePlateLettersKeyDown}
                inputMode="text"
                pattern="[A-Z]{1,3}"
                maxLength={LICENSE_PLATE_LETTERS_LENGTH}
                className="h-9 flex-1 text-center font-mono tracking-widest uppercase"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="shipment-driver-name" className="text-xs text-muted-foreground">{labels.driverName}</Label>
            <Input id="shipment-driver-name" {...register('driverName')} className="h-9" />
          </div>
        </div>
      ) : (
        <>
          <DetailRow label={labels.vehiclePlate} value={shipment.vehiclePlate ?? '-'} />
          <DetailRow label={labels.driverName} value={shipment.driverName ?? '-'} />
        </>
      )}
      <DetailRow label={labels.arrivedAt} value={formatDate(shipment.arrivedAt)} />
      <DetailRow label={labels.departedAt} value={formatDate(shipment.departedAt)} />
      <DetailRow label={labels.factory} value={shipment.factory?.code ?? '-'} />
      <DetailRow label={labels.storageLocation} value={shipment.storageLocation?.code ?? '-'} />
    </SectionCard>
  )
}

function ProgressTimeline({
  currentStatus,
  labels,
}: {
  currentStatus: InboundShipmentStatus
  labels: Record<string, string>
}) {
  const currentIndex = getCurrentStepIndex(currentStatus)

  return (
    <Card className="p-4">
      <div className="space-y-3">
        {STATUS_FLOW.map((status, index) => {
          const isComplete = index <= currentIndex
          const Icon = isComplete ? CheckCircle : Clock

          return (
            <div key={status} className="flex items-center gap-3">
              <div
                className={cn(
                  'h-8 w-8 rounded-full flex items-center justify-center',
                  isComplete ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <span className={cn('text-sm', isComplete ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                {labels[status] ?? status}
              </span>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function buildHeaderContent(
  shipment: InboundShipment,
  statusConfig: StatusConfig,
  shipmentNumberLabel: string,
) {
  return (
    <div className="flex items-start justify-between gap-3 text-white">
      <div className="min-w-0">
        <p className="text-xs text-white/75">{shipmentNumberLabel}</p>
        <h2 className="truncate text-xl font-bold tracking-wide">{shipment.shipmentNumber}</h2>
      </div>
      <Badge variant="secondary" className={cn('shrink-0 text-xs', statusConfig.className)}>
        {statusConfig.label}
      </Badge>
    </div>
  )
}

function InboundShipmentSapSyncTab({ shipment }: { shipment: InboundShipment }) {
  const { t } = useTranslation()
  const [jobs, setJobs] = useState<SapSyncJob[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedJob, setSelectedJob] = useState<SapSyncJob | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const entityIds = buildSapEntityIds(shipment)

  const fetchJobs = async () => {
    if (entityIds.length === 0) return

    setLoading(true)
    try {
      const result = await sapSyncApi.getSyncJobs({
        entityIds,
        sapOperations: INBOUND_SAP_OPERATIONS,
        sortField: 'createdAt',
        sortOrder: 'desc',
      })
      setJobs(result.data)
    } catch {
      setJobs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment.id, shipment.shipmentNumber, shipment.goodsReceipt?.id])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <RefreshCw className="mb-3 h-10 w-10 text-muted-foreground/30" />
        <p className="text-sm font-medium text-muted-foreground">
          {t('orders.sapSyncNoJobs')}
        </p>
        <p className="mt-1 text-xs text-muted-foreground/70">
          {t('orders.sapSyncNoJobsDescription')}
        </p>
        <button
          onClick={fetchJobs}
          className="mt-4 inline-flex items-center gap-2 text-xs text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t('sapSync.refresh')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {jobs.length} {t('sapSync.records')}
        </p>
        <button
          onClick={fetchJobs}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {t('sapSync.refresh')}
        </button>
      </div>

      <SyncJobsTable
        jobs={jobs}
        selectedRowId={selectedJob != null ? String(selectedJob.id) : null}
        onRowClick={(job) => {
          setSelectedJob(job)
          setDetailOpen(true)
        }}
      />

      <SyncJobDetailDrawer
        job={selectedJob}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  )
}

function InboundShipmentTimelineTab({ shipment }: { shipment: InboundShipment }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'incomingMaterials' })
  const [events, setEvents] = useState<InboundShipmentTimelineEventDto[]>([])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const i18n = t('inboundShipments', { returnObjects: true }) as Record<string, unknown>
  const detailI18n = (i18n?.detail as Record<string, string>) ?? {}
  const timelineActionLabels = (detailI18n.timelineActions as unknown as Record<string, string>) ?? {}

  const fetchTimeline = async () => {
    setLoading(true)
    setErrorMessage(null)
    try {
      const result = await fetchInboundShipmentTimeline(shipment.shipmentNumber)
      setEvents(result.data)
    } catch (error) {
      setEvents([])
      setErrorMessage(getTimelineErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTimeline()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment.shipmentNumber])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Clock className="mb-3 h-10 w-10 text-red-500/40" />
        <p className="text-sm font-medium text-red-500">{detailI18n.timelineError ?? 'Timeline failed to load'}</p>
        <p className="mt-1 text-xs text-muted-foreground">{errorMessage}</p>
        <button
          onClick={fetchTimeline}
          className="mt-4 inline-flex items-center gap-2 text-xs text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {detailI18n.refreshTimeline ?? 'Refresh timeline'}
        </button>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Clock className="mb-3 h-10 w-10 text-muted-foreground/30" />
        <p className="text-sm font-medium text-muted-foreground">{detailI18n.noTimeline ?? 'No timeline events yet'}</p>
        <button
          onClick={fetchTimeline}
          className="mt-4 inline-flex items-center gap-2 text-xs text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {detailI18n.refreshTimeline ?? 'Refresh timeline'}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {events.length} {detailI18n.timelineEvents ?? 'events'}
        </p>
        <button
          onClick={fetchTimeline}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {detailI18n.refreshTimeline ?? 'Refresh timeline'}
        </button>
      </div>

      <div className="space-y-3">
        {events.map((event) => {
          const success = event.metadata?.success
          const actionLabel = getTimelineActionLabel(event.action, timelineActionLabels)
          const statusLabel = success === false ? (detailI18n.failed ?? 'Failed') : (detailI18n.success ?? 'Success')
          const Icon = success === false ? Clock : CheckCircle

          return (
            <Card key={event.id} className="p-4">
              <div className="flex gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">{actionLabel}</p>
                    <Badge variant="outline" className={cn('text-xs', getTimelineSuccessClass(success))}>
                      {statusLabel}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{formatDate(event.createdAt)}</p>
                  {event.description ? (
                    <p className="mt-2 text-sm text-muted-foreground">{event.description}</p>
                  ) : null}
                  <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                    <DetailRow label={detailI18n.weightKg ?? 'Weight'} value={formatWeight(event.metadata?.weightKg)} />
                    <DetailRow label={detailI18n.netWeightKg ?? 'Net Weight'} value={formatWeight(event.metadata?.netWeightKg)} />
                    <DetailRow label={detailI18n.timelineStep ?? 'Step'} value={event.metadata?.step ?? '-'} />
                    <DetailRow label={detailI18n.timelineStatus ?? 'Status'} value={event.metadata?.status ?? '-'} />
                  </div>
                  {event.metadata?.error ? (
                    <p className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-600 dark:bg-red-950/30 dark:text-red-300">
                      {event.metadata.error}
                    </p>
                  ) : null}
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export function InboundShipmentDetailDrawer({ shipment, isOpen, onClose, onUpdate, isUpdating }: InboundShipmentDetailDrawerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'incomingMaterials' })
  const isRTL = useIsRTL()
  const [activeTab, setActiveTab] = useState<InboundShipmentDrawerTab>('overview')
  const [showSapSyncTab, setShowSapSyncTab] = useState(false)

  const i18n = t('inboundShipments', { returnObjects: true }) as Record<string, unknown>
  const rawDetailI18n = isRecord(i18n?.detail) ? i18n.detail : {}
  const detailI18n = getStringRecord(rawDetailI18n)
  const timelineLabels = getTimelineLabels(rawDetailI18n)
  const statusLabels = (i18n?.statusFilter as Record<string, string>) ?? {}
  const sapLabels = (i18n?.sapSyncStatusLabel as Record<string, string>) ?? {}

  useEffect(() => {
    if (!isOpen) return

    setShowSapSyncTab(shouldShowInboundShipmentSapSyncTab())
  }, [isOpen])

  useEffect(() => {
    if (!showSapSyncTab && activeTab === 'sap-sync') {
      setActiveTab('overview')
    }
  }, [activeTab, showSapSyncTab])

  if (!shipment) {
    return (
      <BaseDrawer
        open={isOpen}
        onOpenChange={(open) => { if (!open) onClose() }}
        fixedWidth={640}
        tabletWidth="88vw"
        mobileWidth="100vw"
        side="right"
        isRTL={isRTL}
        title={detailI18n.title ?? 'Shipment Details'}
      >
        {null}
      </BaseDrawer>
    )
  }

  const statusConfig = getShipmentStatusConfig(shipment.status, statusLabels)
  const sapLabel = shipment.sapSyncStatus ? (sapLabels[shipment.sapSyncStatus] ?? shipment.sapSyncStatus) : '-'
  const title = `${detailI18n.title ?? 'Shipment Details'} ${shipment.shipmentNumber}`
  const shouldShowOverview = activeTab === 'overview'
  const shouldShowTimeline = activeTab === 'timeline'
  const tabsContent = (
    <div className="border-b border-border/20 bg-slate-50/50 px-4 py-3 dark:bg-slate-800/30">
      <div className="glass-card flex overflow-hidden rounded-2xl border border-border/50">
        <button
          onClick={() => setActiveTab('overview')}
          className={cn(
            'relative inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-[background-color,color]',
            activeTab === 'overview'
              ? 'bg-primary/15 font-semibold text-primary'
              : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
          )}
        >
          <Truck className={cn('h-4 w-4', activeTab === 'overview' && 'text-primary')} />
          {detailI18n.overview ?? 'Overview'}
          {activeTab === 'overview' ? (
            <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t-full bg-primary" />
          ) : null}
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          className={cn(
            'relative inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-[background-color,color]',
            activeTab === 'timeline'
              ? 'bg-primary/15 font-semibold text-primary'
              : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
          )}
        >
          <History className={cn('h-4 w-4', activeTab === 'timeline' && 'text-primary')} />
          {timelineLabels.title}
          {activeTab === 'timeline' ? (
            <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t-full bg-primary" />
          ) : null}
        </button>
        {showSapSyncTab ? (
          <button
            onClick={() => setActiveTab('sap-sync')}
            className={cn(
              'relative inline-flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-[background-color,color]',
              activeTab === 'sap-sync'
                ? 'bg-primary/15 font-semibold text-primary'
                : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
            )}
          >
            <RefreshCw className={cn('h-4 w-4', activeTab === 'sap-sync' && 'text-primary')} />
            {detailI18n.syncInfo ?? 'SAP Sync'}
            {activeTab === 'sap-sync' ? (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t-full bg-primary" />
            ) : null}
          </button>
        ) : null}
      </div>
    </div>
  )

  return (
    <BaseDrawer
      open={isOpen}
      onOpenChange={(open) => { if (!open) onClose() }}
      fixedWidth={680}
      tabletWidth="88vw"
      mobileWidth="100vw"
      side="right"
      isRTL={isRTL}
      title={title}
      headerGradient="from-slate-900 to-slate-700"
      headerContent={buildHeaderContent(
        shipment,
        statusConfig,
        typeof i18n?.shipmentNumber === 'string' ? i18n.shipmentNumber : 'Shipment Number',
      )}
      stickyContent={tabsContent}
      contentClassName="bg-muted/20"
    >
      <div className="p-4 space-y-4">
        {shouldShowOverview ? (
          <>
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard
            label={typeof i18n?.grossWeight === 'string' ? i18n.grossWeight : 'Gross Weight'}
            value={formatWeight(shipment.weighbridgeTicket?.grossWeight)}
            icon={Scale}
          />
          <MetricCard
            label={typeof i18n?.tareWeight === 'string' ? i18n.tareWeight : 'Tare Weight'}
            value={formatWeight(shipment.weighbridgeTicket?.tareWeight)}
            icon={Truck}
          />
          <MetricCard
            label={typeof i18n?.netWeight === 'string' ? i18n.netWeight : 'Net Weight'}
            value={formatWeight(shipment.weighbridgeTicket?.netWeight)}
            icon={Package}
          />
        </div>

        <ProgressTimeline currentStatus={shipment.status} labels={statusLabels} />

        <ShipmentInfoSection
          shipment={shipment}
          onUpdate={onUpdate}
          isUpdating={isUpdating}
          labels={{
            shipmentInfo: detailI18n.shipmentInfo ?? 'Shipment Information',
            vehiclePlate: typeof i18n?.vehiclePlate === 'string' ? i18n.vehiclePlate : 'Vehicle Plate',
            driverName: detailI18n.driverName ?? 'Driver Name',
            arrivedAt: typeof i18n?.arrivedAt === 'string' ? i18n.arrivedAt : 'Arrived At',
            departedAt: detailI18n.departedAt ?? 'Departed At',
            factory: detailI18n.factory ?? 'Factory',
            storageLocation: detailI18n.storageLocation ?? 'Storage Location',
            edit: detailI18n.edit ?? 'Edit',
            save: detailI18n.save ?? 'Save',
            cancel: detailI18n.cancel ?? 'Cancel',
          }}
        />

        <Accordion type="single" collapsible className="space-y-3">
          <AccordionItem value="purchase-order" className="rounded-xl border border-border/50 bg-card px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">{typeof i18n?.purchaseOrder === 'string' ? i18n.purchaseOrder : 'Purchase Order'}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <DetailRow label={detailI18n.poNumber ?? 'PO Number'} value={shipment.purchaseOrder?.poNumber ?? '-'} />
              <DetailRow label={detailI18n.sapPoNumber ?? 'SAP PO Number'} value={shipment.purchaseOrder?.sapPurchaseOrderNumber ?? '-'} />
              <DetailRow label={detailI18n.sapItemNumber ?? 'SAP Item'} value={shipment.purchaseOrderLine?.sapItemNumber ?? '-'} />
              <DetailRow label={detailI18n.material ?? 'Material'} value={shipment.purchaseOrderLine?.material?.name ?? '-'} />
              <DetailRow label={detailI18n.orderedQuantity ?? 'Ordered Quantity'} value={`${shipment.purchaseOrderLine?.orderedQuantity ?? '-'} ${shipment.purchaseOrderLine?.uom ?? ''}`} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="weight-info" className="rounded-xl border border-border/50 bg-card px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">{detailI18n.weightInfo ?? 'Weight Information'}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <DetailRow label={detailI18n.ticketNumber ?? 'Ticket Number'} value={shipment.weighbridgeTicket?.ticketNumber ?? '-'} />
              <DetailRow label={typeof i18n?.grossWeight === 'string' ? i18n.grossWeight : 'Gross Weight'} value={formatWeight(shipment.weighbridgeTicket?.grossWeight)} />
              <DetailRow label={typeof i18n?.tareWeight === 'string' ? i18n.tareWeight : 'Tare Weight'} value={formatWeight(shipment.weighbridgeTicket?.tareWeight)} />
              <DetailRow label={typeof i18n?.netWeight === 'string' ? i18n.netWeight : 'Net Weight'} value={formatWeight(shipment.weighbridgeTicket?.netWeight)} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="sap-sync" className="rounded-xl border border-border/50 bg-card px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">{detailI18n.syncInfo ?? 'SAP Sync'}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <DetailRow
                label={typeof i18n?.sapSyncStatus === 'string' ? i18n.sapSyncStatus : 'SAP Sync Status'}
                value={
                  <Badge variant="outline" className={cn('text-xs', getSapSyncClass(shipment.sapSyncStatus))}>
                    {sapLabel}
                  </Badge>
                }
              />
              <DetailRow label={detailI18n.firstWeightSyncedAt ?? 'First Weight Synced At'} value={formatDate(shipment.sapFirstWeightSyncedAt)} />
              <DetailRow label={detailI18n.secondWeightSyncedAt ?? 'Second Weight Synced At'} value={formatDate(shipment.sapSecondWeightSyncedAt)} />
              <DetailRow label={detailI18n.grnSyncedAt ?? 'GRN Synced At'} value={formatDate(shipment.sapGrnSyncedAt)} />
              <DetailRow label={typeof i18n?.grnNumber === 'string' ? i18n.grnNumber : 'GRN Number'} value={shipment.goodsReceipt?.grnNumber ?? '-'} />
              <DetailRow label={typeof i18n?.sapMaterialDocument === 'string' ? i18n.sapMaterialDocument : 'SAP Material Document'} value={shipment.goodsReceipt?.sapMaterialDocument ?? '-'} />
              {shipment.sapSyncError ? (
                <DetailRow
                  label={detailI18n.error ?? 'Error'}
                  value={<span className="text-xs text-red-500">{shipment.sapSyncError}</span>}
                />
              ) : null}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
          </>
        ) : shouldShowTimeline ? (
          <InboundShipmentTimelineTab shipment={shipment} />
        ) : (
          <InboundShipmentSapSyncTab shipment={shipment} />
        )}
      </div>
    </BaseDrawer>
  )
}
